import { useAuth } from "@clerk/react"
import { Pencil } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  ArtworkDropTarget,
  ArtworkOverlayControls,
} from "../cards/ArtworkControls"
import { type CardEditingCallbacks, CardFace } from "../cards/CardFace"
import { CostBadge } from "../cards/CostBadge"
import { PortalTargetProvider } from "../cards/PortalTarget"
import {
  AttackIcon,
  ExhaustIcon,
  HealthIcon,
  SKILL_ICON,
} from "../cards/SkillIcon"
import { getCardValidationIssueLabel } from "../cards/validationMessages"
import { useIdbState } from "../hooks/useIdbState"
import { useStoredState } from "../hooks/useStoredState"
import { getSkillName } from "../i18n/gameContent"
import { useI18n } from "../i18n/useI18n"
import { SKILL_MAP } from "../match/engine/content/skills"
import {
  computeSkillCostBreakdown,
  computeStatCost,
} from "../match/engine/costs"
import { validateCard } from "../match/engine/validator/cards"
import {
  ARTWORK_ASSET_STORAGE_KEY,
  ARTWORK_ASSET_STORAGE_VERSION,
  ArtworkAssetStoreSchema,
  collectArtworkAssets,
  EMPTY_ARTWORK_ASSET_STORE,
  upsertArtworkAssets,
} from "../media/assets"
import { getArtworkErrorLabel } from "../media/errors"
import { processArtworkFile } from "../media/processArtwork"
import { useArtworkSyncMutation } from "../media/query"
import { requestTurnstileToken } from "../media/turnstile"
import {
  ARTWORK_UPLOAD_STORAGE_KEY,
  ARTWORK_UPLOAD_STORAGE_VERSION,
  ArtworkUploadStoreSchema,
  createArtworkUploadRecord,
  EMPTY_ARTWORK_UPLOAD_STORE,
  updateArtworkUploadStatus,
  upsertArtworkUpload,
} from "../media/uploads"
import type {
  AuthoredCard,
  BasicBattleBotCard,
  BetterBattleBotCard,
  CardArtwork,
  SparkBotCard,
  SystemBattleBotsConfig,
} from "../schemas"
import {
  useSyncSystemBattleBotsMutation,
  useSystemBattleBotsQuery,
} from "./query"
import {
  EMPTY_SYSTEM_BATTLE_BOTS,
  SYSTEM_BATTLE_BOTS_STORAGE_KEY,
  SYSTEM_BATTLE_BOTS_STORAGE_VERSION,
  SystemBattleBotsStateSchema,
} from "./storage"

type SystemBattleBotCard =
  | BasicBattleBotCard
  | SparkBotCard
  | BetterBattleBotCard
type SystemBattleBotKey = "basicBattleBot" | "sparkBot" | "betterBattleBot"

type ArtworkSyncStatus = "localOnly" | "retrying" | "synced" | "syncing" | null

function getArtworkSyncStatusLabel(
  t: ReturnType<typeof useI18n>["t"],
  status: Exclude<ArtworkSyncStatus, null>,
) {
  switch (status) {
    case "localOnly":
      return t("ui.artwork.sync.localOnly")
    case "retrying":
      return t("ui.artwork.sync.retrying")
    case "synced":
      return t("ui.artwork.sync.synced")
    case "syncing":
      return t("ui.artwork.sync.syncing")
  }
}

function StatCell({
  kind,
  value,
}: {
  kind: "attack" | "health"
  value: number
}) {
  const Icon = kind === "attack" ? AttackIcon : HealthIcon
  return (
    <span className={`inline-stat inline-stat-${kind}`}>
      <Icon size={14} strokeWidth={2} />
      {value}
    </span>
  )
}

function computeDerivedCost(card: AuthoredCard): number {
  const statCost = computeStatCost(card.stats.attack, card.stats.health)
  const skillCosts = computeSkillCostBreakdown(
    card.selectedSkills,
    SKILL_MAP,
    statCost + card.authoredCostInputs.secondaryColorSurcharge,
  )
  return Math.ceil(
    statCost +
      skillCosts.skillCostDelta +
      skillCosts.nonExhaustSurcharge +
      skillCosts.selfDestructSurcharge +
      card.authoredCostInputs.secondaryColorSurcharge,
  )
}

function normalizeBattleBotCard<T extends SystemBattleBotCard>(card: T): T {
  const secondaryColorSurcharge: 0 | 0.5 =
    card.secondaryColor && card.primaryColor !== "colorless" ? 0.5 : 0
  const normalized = {
    ...card,
    secondaryColor: card.secondaryColor || undefined,
    authoredCostInputs: {
      ...card.authoredCostInputs,
      secondaryColorSurcharge,
    },
  }
  return {
    ...normalized,
    derivedCost: computeDerivedCost(normalized),
  } as T
}

function updateCard<T extends SystemBattleBotCard>(
  card: T,
  patch: Partial<T> | ((current: T) => Partial<T>),
): T {
  const nextPatch = typeof patch === "function" ? patch(card) : patch
  return normalizeBattleBotCard({
    ...card,
    ...nextPatch,
  } as T)
}

function getRowTitle(
  t: ReturnType<typeof useI18n>["t"],
  key: SystemBattleBotKey,
) {
  if (key === "basicBattleBot") return t("admin.systemBattleBots.basicTitle")
  if (key === "sparkBot") return t("admin.systemBattleBots.sparkTitle")
  return t("admin.systemBattleBots.betterTitle")
}

export function SystemBattleBotsPanel() {
  const { t } = useI18n()
  const { userId } = useAuth()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null)
  const handleDialogRef = useCallback((el: HTMLDialogElement | null) => {
    dialogRef.current = el
    setDialogEl(el)
  }, [])
  const [storedConfig, setStoredConfig] = useStoredState(
    SYSTEM_BATTLE_BOTS_STORAGE_KEY,
    SystemBattleBotsStateSchema,
    {
      defaultValue: EMPTY_SYSTEM_BATTLE_BOTS,
      version: SYSTEM_BATTLE_BOTS_STORAGE_VERSION,
    },
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [editingKey, setEditingKey] = useState<SystemBattleBotKey | null>(null)
  const [editingCard, setEditingCard] = useState<SystemBattleBotCard | null>(
    null,
  )

  const artworkSyncMutation = useArtworkSyncMutation()
  const [artworkError, setArtworkError] = useState<string | null>(null)
  const [artworkBusy, setArtworkBusy] = useState(false)
  const [artworkSyncBusy, setArtworkSyncBusy] = useState(false)
  const [artworkSyncStatus, setArtworkSyncStatus] =
    useState<ArtworkSyncStatus>(null)
  const [, setArtworkUploads] = useStoredState(
    ARTWORK_UPLOAD_STORAGE_KEY,
    ArtworkUploadStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_UPLOAD_STORE,
      version: ARTWORK_UPLOAD_STORAGE_VERSION,
    },
  )
  const [artworkAssets, , setArtworkAssets] = useIdbState(
    ARTWORK_ASSET_STORAGE_KEY,
    ArtworkAssetStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_ASSET_STORE,
      version: ARTWORK_ASSET_STORAGE_VERSION,
    },
  )

  const systemBattleBotsQuery = useSystemBattleBotsQuery()
  const syncSystemBattleBots = useSyncSystemBattleBotsMutation()

  useEffect(() => {
    if (!systemBattleBotsQuery.data) {
      return
    }
    setStoredConfig(systemBattleBotsQuery.data)
    setError(null)
  }, [setStoredConfig, systemBattleBotsQuery.data])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (editingKey && !dialog.open) {
      dialog.showModal()
      return
    }
    if (!editingKey && dialog.open) {
      dialog.close()
    }
  }, [editingKey])

  const rows = useMemo(
    () => [
      {
        key: "basicBattleBot" as const,
        card: normalizeBattleBotCard(storedConfig.basicBattleBot),
      },
      {
        key: "sparkBot" as const,
        card: normalizeBattleBotCard(storedConfig.sparkBot),
      },
      {
        key: "betterBattleBot" as const,
        card: normalizeBattleBotCard(storedConfig.betterBattleBot),
      },
    ],
    [storedConfig],
  )

  const editingValidation = useMemo(
    () => (editingCard ? validateCard(editingCard) : null),
    [editingCard],
  )
  const editingIssues = editingValidation?.issues ?? []
  const canSaveEditing = Boolean(
    editingCard && editingValidation?.valid && !saving,
  )

  function openEditor(key: SystemBattleBotKey) {
    setEditingKey(key)
    const card =
      key === "basicBattleBot"
        ? storedConfig.basicBattleBot
        : key === "sparkBot"
          ? storedConfig.sparkBot
          : storedConfig.betterBattleBot
    setEditingCard(normalizeBattleBotCard(card))
    setError(null)
    setStatus(null)
    setArtworkError(null)
    setArtworkBusy(false)
    setArtworkSyncBusy(false)
    setArtworkSyncStatus(null)
  }

  function closeEditor() {
    setEditingKey(null)
    setEditingCard(null)
    setArtworkError(null)
    setArtworkBusy(false)
    setArtworkSyncBusy(false)
    setArtworkSyncStatus(null)
  }

  const syncArtworkEnvelope = async (artwork: CardArtwork) => {
    if (!userId) {
      throw new Error(t("ui.artwork.error.syncUnavailable"))
    }
    const assets = collectArtworkAssets(artwork, artworkAssets)
    if (assets.length === 0) {
      throw new Error(t("ui.artwork.error.noLocalVariants"))
    }
    const turnstileToken = await requestTurnstileToken()
    return artworkSyncMutation.mutateAsync({
      payload: { artwork, assets },
      turnstileToken,
    })
  }

  const handleArtworkFile = async (file: File | null) => {
    if (!file) return

    const uploadRecord = createArtworkUploadRecord({
      originalFilename: file.name,
      mimeType: file.type || "application/octet-stream",
      byteLength: file.size,
    })

    setArtworkUploads((previous) => upsertArtworkUpload(previous, uploadRecord))
    setArtworkBusy(true)
    setArtworkError(null)
    setArtworkSyncStatus(null)

    try {
      const processed = await processArtworkFile(file)
      setArtworkAssets((previous) =>
        upsertArtworkAssets(previous, processed.assets),
      )
      let syncedArtwork = processed.artwork
      try {
        setArtworkSyncBusy(true)
        setArtworkSyncStatus("syncing")
        if (!userId) {
          throw new Error(t("ui.artwork.error.syncUnavailable"))
        }
        const turnstileToken = await requestTurnstileToken()
        const synced = await artworkSyncMutation.mutateAsync({
          payload: processed,
          turnstileToken,
        })
        syncedArtwork = synced.artwork
        setArtworkSyncStatus("synced")
        setArtworkError(null)
      } catch (syncError) {
        const message =
          getArtworkErrorLabel(t, syncError) ??
          t("ui.artwork.error.uploadFailed")
        setArtworkError(message)
        setArtworkSyncStatus("localOnly")
      } finally {
        setArtworkSyncBusy(false)
      }
      setArtworkUploads((previous) =>
        updateArtworkUploadStatus(previous, uploadRecord.uploadId, "ready", {
          hash: syncedArtwork.hash,
          variants: syncedArtwork.variants,
        }),
      )
      setEditingCard((previous) =>
        previous
          ? updateCard(previous, {
              artwork: syncedArtwork,
            } as Partial<SystemBattleBotCard>)
          : previous,
      )
    } catch (uploadError) {
      const message =
        getArtworkErrorLabel(t, uploadError) ??
        t("ui.artwork.error.uploadFailed")
      setArtworkError(message)
      setArtworkUploads((previous) =>
        updateArtworkUploadStatus(previous, uploadRecord.uploadId, "rejected", {
          rejectionReason: message,
        }),
      )
    } finally {
      setArtworkBusy(false)
    }
  }

  const handleRetryArtworkSync = async () => {
    if (!editingCard?.artwork) return

    setArtworkSyncBusy(true)
    setArtworkSyncStatus("retrying")
    setArtworkError(null)
    try {
      const synced = await syncArtworkEnvelope(editingCard.artwork)
      setEditingCard((previous) =>
        previous
          ? updateCard(previous, {
              artwork: synced.artwork,
            } as Partial<SystemBattleBotCard>)
          : previous,
      )
      setArtworkSyncStatus("synced")
    } catch (retryError) {
      const message =
        getArtworkErrorLabel(t, retryError) ??
        t("ui.artwork.error.uploadFailed")
      setArtworkError(message)
      setArtworkSyncStatus("localOnly")
    } finally {
      setArtworkSyncBusy(false)
    }
  }

  const editingCallbacks: CardEditingCallbacks | undefined = editingCard
    ? {
        onNameChange: (name) =>
          setEditingCard((previous) =>
            previous ? updateCard(previous, { name }) : previous,
          ),
        onPrimaryColorChange: (primaryColor) =>
          setEditingCard((previous) =>
            previous ? updateCard(previous, { primaryColor }) : previous,
          ),
        onSecondaryColorChange: (secondaryColor) =>
          setEditingCard((previous) =>
            previous ? updateCard(previous, { secondaryColor }) : previous,
          ),
        onAttackChange: (attack) =>
          setEditingCard((previous) =>
            previous
              ? updateCard(previous, (current) => ({
                  stats: { ...current.stats, attack },
                }))
              : previous,
          ),
        onHealthChange: (health) =>
          setEditingCard((previous) =>
            previous
              ? updateCard(previous, (current) => ({
                  stats: { ...current.stats, health },
                }))
              : previous,
          ),
        onSkillsChange: (selectedSkills) =>
          setEditingCard((previous) =>
            previous ? updateCard(previous, { selectedSkills }) : previous,
          ),
      }
    : undefined

  async function handleSaveEditing() {
    if (!userId || !editingKey || !editingCard || !editingValidation?.valid) {
      return
    }

    const previousConfig = storedConfig
    let nextConfig: SystemBattleBotsConfig
    if (
      editingKey === "basicBattleBot" &&
      editingCard.id === "basic_battle_bot"
    ) {
      nextConfig = {
        ...storedConfig,
        basicBattleBot: normalizeBattleBotCard(editingCard),
        updatedAt: new Date().toISOString(),
      }
    } else if (editingKey === "sparkBot" && editingCard.id === "spark_bot") {
      nextConfig = {
        ...storedConfig,
        sparkBot: normalizeBattleBotCard(editingCard),
        updatedAt: new Date().toISOString(),
      }
    } else if (
      editingKey === "betterBattleBot" &&
      editingCard.id === "better_battle_bot"
    ) {
      nextConfig = {
        ...storedConfig,
        betterBattleBot: normalizeBattleBotCard(editingCard),
        updatedAt: new Date().toISOString(),
      }
    } else {
      return
    }

    setSaving(true)
    setError(null)
    setStatus(null)
    setStoredConfig(nextConfig)

    try {
      const synced = await syncSystemBattleBots.mutateAsync(nextConfig)
      setStoredConfig(synced)
      setStatus(t("admin.systemBattleBots.saved"))
      closeEditor()
    } catch (nextError) {
      setStoredConfig(previousConfig)
      setError(
        nextError instanceof Error
          ? nextError.message
          : t("admin.systemBattleBots.error.saveFailed"),
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">{t("admin.systemBattleBots.eyebrow")}</p>
          <h2>{t("admin.systemBattleBots.title")}</h2>
          <p className="meta">{t("admin.systemBattleBots.description")}</p>
        </div>
      </div>

      {systemBattleBotsQuery.isPending ? (
        <p className="meta">{t("common.loading")}</p>
      ) : null}
      {systemBattleBotsQuery.error instanceof Error && !error ? (
        <strong className="field-error">
          {systemBattleBotsQuery.error.message}
        </strong>
      ) : null}
      {error ? <strong className="field-error">{error}</strong> : null}
      {status ? <p className="meta">{status}</p> : null}

      <div className="table-frame">
        <table className="data-table system-battle-bots-table">
          <thead>
            <tr>
              <th>{t("common.name")}</th>
              <th>{t("common.attack")}</th>
              <th>{t("common.health")}</th>
              <th>{t("common.cost")}</th>
              <th>{t("common.skills")}</th>
              <th>{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, card }) => (
              <tr key={key}>
                <td>
                  <strong>{card.name}</strong>
                </td>
                <td>
                  <StatCell kind="attack" value={card.stats.attack} />
                </td>
                <td>
                  <StatCell kind="health" value={card.stats.health} />
                </td>
                <td>
                  <CostBadge
                    cost={card.derivedCost ?? 0}
                    size="s"
                    variant="subtle"
                  />
                </td>
                <td className="system-battle-bot-skills-cell">
                  {card.selectedSkills.length === 0 ? (
                    <span className="meta">{t("common.none")}</span>
                  ) : (
                    <ul className="skill-summary-list">
                      {card.selectedSkills.map((sel) => {
                        const Icon = SKILL_ICON[sel.skillId] ?? ExhaustIcon
                        return (
                          <li className="skill-summary-item" key={sel.skillId}>
                            <span className="skill-summary-icon">
                              <Icon size={14} strokeWidth={2} />
                            </span>
                            <span className="skill-summary-label">
                              {getSkillName(t, sel.skillId)}
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </td>
                <td className="system-battle-bot-actions-cell">
                  <button
                    className="table-action-button"
                    onClick={() => openEditor(key)}
                    title={t("common.edit")}
                    type="button"
                  >
                    <Pencil size={16} strokeWidth={2} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dialog
        className="card-editor-dialog card-editor-dialog-centered system-battle-bots-dialog"
        onCancel={(event) => {
          event.preventDefault()
          closeEditor()
        }}
        ref={handleDialogRef}
      >
        <PortalTargetProvider value={dialogEl}>
          <div className="card-editor-inner">
            <div className="card-editor-header">
              <h2>
                {editingKey
                  ? t("admin.systemBattleBots.editTitle", {
                      name: getRowTitle(t, editingKey),
                    })
                  : t("admin.systemBattleBots.title")}
              </h2>
              <button
                aria-label={t("common.close")}
                className="card-editor-close"
                onClick={closeEditor}
                type="button"
              >
                ✕
              </button>
            </div>

            {editingCard ? (
              <form
                className="card-editor-form"
                onSubmit={(event) => {
                  event.preventDefault()
                  void handleSaveEditing()
                }}
              >
                <div className="card-editor-body">
                  <CardFace
                    artwork={editingCard.artwork}
                    artworkAssets={artworkAssets}
                    artworkEditorOverlay={
                      <>
                        <ArtworkDropTarget
                          addLabel={t("ui.artwork.addImage")}
                          artwork={editingCard.artwork}
                          busy={artworkBusy}
                          onSelectFile={(file) => void handleArtworkFile(file)}
                          replaceLabel={t("ui.artwork.replaceImage")}
                        />
                        <ArtworkOverlayControls
                          error={artworkError}
                          onRemove={
                            editingCard.artwork
                              ? () =>
                                  setEditingCard((previous) =>
                                    previous
                                      ? updateCard(previous, {
                                          artwork: undefined,
                                        } as Partial<SystemBattleBotCard>)
                                      : previous,
                                  )
                              : undefined
                          }
                          onRetrySync={
                            editingCard.artwork &&
                            artworkSyncStatus === "localOnly"
                              ? () => void handleRetryArtworkSync()
                              : undefined
                          }
                          removeLabel={t("ui.artwork.removeImage")}
                          retrySyncBusy={artworkSyncBusy}
                          retrySyncLabel={t("ui.artwork.retrySync")}
                          retryingLabel={t("ui.artwork.retrying")}
                          syncStatusLabel={
                            artworkSyncStatus && artworkSyncStatus !== "synced"
                              ? getArtworkSyncStatusLabel(t, artworkSyncStatus)
                              : null
                          }
                        />
                      </>
                    }
                    cost={editingCard.derivedCost}
                    editing={editingCallbacks}
                    name={editingCard.name}
                    primaryColor={editingCard.primaryColor}
                    secondaryColor={editingCard.secondaryColor}
                    skills={editingCard.selectedSkills}
                    size="lg"
                    stats={editingCard.stats}
                    style={{ width: "min(20rem, 80vw)" }}
                  />
                </div>

                {editingIssues.length > 0 ? (
                  <div className="system-battle-bot-issues">
                    {editingIssues.map((issue) => (
                      <p
                        className="meta"
                        key={`${issue.code}:${issue.path ?? ""}`}
                      >
                        {getCardValidationIssueLabel(t, issue)}
                      </p>
                    ))}
                  </div>
                ) : null}

                <div className="card-editor-actions">
                  <button
                    className="card-editor-action-button card-editor-action-button-secondary"
                    onClick={closeEditor}
                    type="button"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    className="card-editor-action-button card-editor-action-button-primary"
                    disabled={!canSaveEditing}
                    type="submit"
                  >
                    {saving
                      ? t("admin.systemBattleBots.saving")
                      : t("common.saveChanges")}
                  </button>
                </div>
              </form>
            ) : null}
          </div>
        </PortalTargetProvider>
      </dialog>
    </section>
  )
}
